import React, { memo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaTimes } from 'react-icons/fa';
import useNavMenu from '../../../hooks/useNavMenu';
import { isNavParentActive, isNavPathActive } from '../../../config/navMenuConfig';
import { isSuperAdminEmail } from '../../../config/superAdmin';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Drawer de navegación completa para móvil (≤767px).
 */
const NavMobileDrawer = memo(function NavMobileDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const menuItems = useNavMenu();
  const { currentUser } = useAuth();
  const [expanded, setExpanded] = useState({});
  const superAdmin = isSuperAdminEmail(currentUser?.email);

  if (!isOpen) return null;

  const toggle = (label) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="e0e3-mobile-nav-drawer fixed inset-y-0 left-0 z-[75] flex w-[min(300px,88vw)] flex-col border-r border-white/[0.08] bg-e0e3-shell shadow-2xl"
        aria-label="Menú de navegación"
      >
        <div className="flex h-12 items-center justify-between border-b border-white/[0.08] px-4">
          <span className="text-sm font-bold text-white">0E3 Workspace</span>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-e0e3-muted hover:bg-white/[0.08]" aria-label="Cerrar menú">
            <FaTimes size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 scrollbar-thin">
          {menuItems.map((item) => {
            const Icon = item.icon;
            if (item.submenu) {
              const parentActive = isNavParentActive(location.pathname, item.submenu);
              const isExp = expanded[item.label] ?? parentActive;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => toggle(item.label)}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium ${parentActive ? 'text-e0e3-accent' : 'text-e0e3-muted'}`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={18} />
                      {item.label}
                    </span>
                    {isExp ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                  </button>
                  {isExp && (
                    <div className="mb-1 ml-4 space-y-0.5 border-l border-white/[0.08] pl-2">
                      {item.submenu.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={onClose}
                          className={`block rounded-lg px-3 py-2 text-sm ${isNavPathActive(location.pathname, sub.path) ? 'bg-e0e3-accent/15 font-semibold text-e0e3-accent' : 'text-e0e3-muted hover:bg-white/[0.06]'}`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.path}
                onClick={onClose}
                className={`mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${isNavPathActive(location.pathname, item.path) ? 'e0e3-nav-link--active text-e0e3-accent' : 'text-e0e3-muted hover:bg-white/[0.06]'}`}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            );
          })}
          {superAdmin && (
            <Link
              to="/admin"
              onClick={onClose}
              className={`mt-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${location.pathname === '/admin' ? 'text-amber-300' : 'text-e0e3-muted'}`}
            >
              Admin
            </Link>
          )}
        </nav>
      </aside>
    </>
  );
});

export default NavMobileDrawer;
